import { useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { DownloadIcon, RefreshCwIcon, UsersIcon } from "lucide-react";

import { CopyButton } from "@/components/copy-button";
import { ErrorState } from "@/components/error-state";
import { EmptyState } from "@/components/page-header";
import { ToneBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AccountStatus, AdminService, type Account, type Group } from "@/gen/wabms/admin/v1/admin_pb";
import { adminKeys } from "@/lib/admin-queries";
import { downloadCsv, slugify, toCsv } from "@/lib/csv";
import { formatAbsolute, formatCount, toDate } from "@/lib/format";

const CSV_HEADER = [
  "wa_id",
  "name",
  "participants",
  "created_at",
  "is_community",
  "parent_wa_id",
  "announce_only",
] as const;

/**
 * Groups are read live from WhatsApp — one info query per fetch, nothing cached
 * server-side. The query is therefore fetched when the tab is opened and then
 * only on an explicit Refresh: polling this would put avoidable traffic on an
 * account WhatsApp is already watching for abuse.
 */
export function GroupsTab({ account }: { account: Account }) {
  const queryClient = useQueryClient();
  const connected = account.status === AccountStatus.CONNECTED;

  const groupsQuery = useQuery(
    AdminService.method.listGroups,
    { accountId: account.accountId },
    { enabled: connected, staleTime: Infinity, refetchOnWindowFocus: false },
  );
  const groups = groupsQuery.data?.groups ?? [];
  const fetchedAt = toDate(groupsQuery.data?.fetchedAt) ?? new Date();

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: adminKeys.groups() });
  }

  function download() {
    const rows = [[...CSV_HEADER], ...groups.map(csvRow)];
    const stamp = format(fetchedAt, "yyyyMMdd-HHmm");
    downloadCsv(`groups-${slugify(account.label)}-${stamp}.csv`, toCsv(rows));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Every group this account is a member of, read live from WhatsApp when you open this tab.
          Nothing is stored. A group JID is a valid batch destination: copy it and submit it with{" "}
          <code className="font-mono text-xs">id_kind: "wa_jid"</code>. It only works for this
          account — a group can only be addressed by an account that is a member of it.
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!connected || groupsQuery.isFetching}
            onClick={() => void refresh()}
          >
            <RefreshCwIcon className={groupsQuery.isFetching ? "animate-spin" : ""} /> Refresh
          </Button>
          <Button size="sm" disabled={groups.length === 0} onClick={download}>
            <DownloadIcon /> Download CSV
          </Button>
        </div>
      </div>

      {!connected ? (
        <EmptyState
          icon={<UsersIcon />}
          title="The account is not connected"
          description="WhatsApp only answers a group listing over a live connection. Reconnect the account, then come back."
        />
      ) : groupsQuery.isError ? (
        <ErrorState
          error={groupsQuery.error}
          title="Could not load groups"
          onRetry={() => void groupsQuery.refetch()}
        />
      ) : groupsQuery.isPending ? (
        <Skeleton className="h-52 rounded-xl" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<UsersIcon />}
          title="This account is in no groups"
          description="WhatsApp reported an empty membership list."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Group JID</TableHead>
                  <TableHead className="text-right">Participants</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.waId}>
                    <TableCell className="font-medium">
                      {group.name || <span className="text-muted-foreground">(no name)</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="font-mono text-xs break-all text-muted-foreground">
                          {group.waId}
                        </code>
                        <CopyButton
                          value={group.waId}
                          variant="ghost"
                          size="xs"
                          label=""
                          className="shrink-0"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(group.participantCount)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatAbsolute(group.createdAt, "—")}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap justify-end gap-1">
                        {group.isCommunity ? <ToneBadge tone="info">community</ToneBadge> : null}
                        {group.parentWaId ? <ToneBadge tone="neutral">in community</ToneBadge> : null}
                        {group.announceOnly ? (
                          <ToneBadge tone="warning">admins only</ToneBadge>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatCount(groups.length)} groups, read {formatAbsolute(groupsQuery.data?.fetchedAt)}.
          </p>
          <p className="text-xs text-muted-foreground">
            In an <strong>admins only</strong> group, sending fails with{" "}
            <code className="font-mono">BLOCKED_BY_RECIPIENT</code> unless this account is an admin.
          </p>
        </>
      )}
    </div>
  );
}

/** One CSV record. Column order matches CSV_HEADER. */
function csvRow(group: Group): string[] {
  const created = toDate(group.createdAt);
  return [
    group.waId,
    group.name,
    String(group.participantCount),
    created ? created.toISOString() : "",
    String(group.isCommunity),
    group.parentWaId,
    String(group.announceOnly),
  ];
}
